import type { GrammarExercise } from '../exercise-types';

// Practice exercises for the Fluency-level topics (plus -ed Pronunciation).
// Advanced difficulty; contexts follow E2E's critical, feminist, empowering
// methodology — women as protagonists of their own stories.
export const fluencyExercises: Record<string, GrammarExercise[]> = {
  'ed-pronunciation': [
    {
      kind: 'multiple-choice',
      prompt: 'In which verb is the -ed ending pronounced /ɪd/, adding a new syllable?',
      options: ['visited', 'laughed', 'played'],
      correctIndex: 0,
      explanation: 'Verbs ending in the sound /t/ or /d/ (visit, want, end) get the /ɪd/ sound: vi-si-ted.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Renata presented her project and the whole room clapped. How does the -ed in clapped sound?',
      options: ['/t/', '/d/', '/ɪd/'],
      correctIndex: 0,
      explanation: 'Clap ends in the unvoiced consonant /p/, so the -ed sounds like /t/ — no new syllable.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The committee allowed more women to join the board. The -ed in allowed sounds like:',
      options: ['/ɪd/', '/d/', '/t/'],
      correctIndex: 1,
      explanation: 'Allow ends in a voiced sound, so the -ed sounds like /d/ and does not create a new syllable.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Tricky one: liked ends in the letter e, but how is its -ed pronounced?',
      options: ['/ɪd/', '/d/', '/t/'],
      correctIndex: 2,
      explanation: 'It\'s the SOUND that matters, not the spelling: like ends in the sound /k/, which is unvoiced, so -ed sounds like /t/.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which verb gains an extra syllable when you add -ed?',
      options: ['washed', 'ended', 'caused'],
      correctIndex: 1,
      explanation: 'End finishes in the sound /d/, so ended is pronounced en-ded, with the new syllable /ɪd/.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'In which verb does the -ed sound like /d/?',
      options: ['begged', 'watched', 'wanted'],
      correctIndex: 0,
      explanation: 'Beg ends in the voiced consonant /g/, so the -ed sounds like /d/.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She faxed the signed contract to her new business partner. The -ed in faxed is pronounced /___/.',
      answers: ['t', '/t/'],
      explanation: 'Fax ends in the letter x but the sound /s/, which is unvoiced — so the -ed sounds like /t/.',
    },
    {
      kind: 'matching',
      instruction: 'Match each verb with the sound of its -ed ending:',
      pairs: [
        { left: 'wanted', right: '/ɪd/' },
        { left: 'watched', right: '/t/' },
        { left: 'played', right: '/d/' },
      ],
      explanation: 'Final /t/ or /d/ → /ɪd/; unvoiced sounds → /t/; voiced sounds → /d/.',
    },
  ],

  'embedded-questions': [
    {
      kind: 'multiple-choice',
      prompt: 'Could you tell me where ___? I\'d like to present my proposal there.',
      options: ['the meeting room is', 'is the meeting room', 'does the meeting room be'],
      correctIndex: 0,
      explanation: 'In an embedded question, the word order goes back to a statement: subject + verb.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Do you know what time ___? I don\'t want to miss the keynote speaker.',
      options: ['does the conference start', 'the conference starts', 'starts the conference'],
      correctIndex: 1,
      explanation: 'Embedded questions drop the auxiliary do/does: ...what time the conference starts.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'They asked me ___ I was available to lead the new project — and I said yes.',
      options: ['whether', 'what', 'that'],
      correctIndex: 0,
      explanation: 'Yes/no questions become embedded with whether or if: They asked me whether/if I was available.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'Do you know where is the director?',
        'Do you know where the director is?',
        'Do you know where does the director is?',
      ],
      correctIndex: 1,
      explanation: 'After the question word, use statement order: where + subject + verb.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Can you tell me ___ the hotel rooms have Wi-Fi? I need it to work during my trip.',
      answers: ['if', 'whether', 'whether or not'],
      explanation: 'Questions without a question word (Do the rooms have Wi-Fi?) are embedded with if or whether.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Direct: Where is the station? → Polite: Could you tell me where the station ___?',
      answers: ['is'],
      explanation: 'The verb moves after the subject in embedded questions: where the station is.',
    },
  ],

  'tag-questions': [
    {
      kind: 'multiple-choice',
      prompt: 'You negotiated a better contract, ___?',
      options: ['didn’t you', 'did you', 'don’t you'],
      correctIndex: 0,
      explanation: 'A positive sentence takes a negative tag, repeating the auxiliary: negotiated → didn\'t you?',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She never gives up on her goals, ___?',
      options: ['doesn’t she', 'does she', 'isn’t she'],
      correctIndex: 1,
      explanation: 'Never makes the sentence negative, so the tag is positive: ...does she?',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I’m leading this meeting, ___?',
      options: ['amn’t I', 'aren’t I', 'am I'],
      correctIndex: 1,
      explanation: 'The tag for I am is the exception aren\'t I? — there is no amn\'t.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Nobody interrupted her presentation, ___?',
      options: ['did they', 'didn’t they', 'did she'],
      correctIndex: 0,
      explanation: 'Nobody is negative and takes they in the tag: Nobody left a message, did they?',
    },
    {
      kind: 'fill-blank',
      prompt: 'You can join our study group tonight, ___ you?',
      answers: ['can’t', 'can\'t', 'cannot'],
      explanation: 'The tag repeats the modal with opposite polarity: can → can\'t you?',
    },
    {
      kind: 'fill-blank',
      prompt: 'You’ll support my proposal at the board meeting, ___ you?',
      answers: ['won’t', 'won\'t', 'will not'],
      explanation: 'With will, the negative tag is won\'t: You\'ll bring other things, won\'t you?',
    },
  ],

  'gerunds-infinitives': [
    {
      kind: 'multiple-choice',
      prompt: 'Marina enjoys ___ in public now — she worked hard to build that confidence.',
      options: ['speaking', 'to speak', 'speak'],
      correctIndex: 0,
      explanation: 'Verbs like enjoy, avoid, consider, and mind are always followed by a gerund.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She is interested in ___ a master’s degree abroad.',
      options: ['to pursue', 'pursuing', 'pursue'],
      correctIndex: 1,
      explanation: 'Gerunds are always used after prepositions: interested in learning, tired of waiting.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'After years of planning, they decided ___ their own business together.',
      options: ['opening', 'to open', 'open'],
      correctIndex: 1,
      explanation: 'Verbs like decide, want, hope, plan, and promise are always followed by an infinitive.',
    },
    {
      kind: 'fill-blank',
      prompt: '___ (travel) alone taught Júlia how capable she really is.',
      answers: ['traveling', 'travelling'],
      explanation: 'When the action itself is the subject of the sentence, use a gerund: Swimming is a great way to stay fit.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She left the party early ___ (catch) her flight to the conference.',
      answers: ['to catch'],
      explanation: 'Use an infinitive to express purpose — the reason for an action.',
    },
    {
      kind: 'matching',
      instruction: 'Match each beginning with the correct ending — gerund or infinitive:',
      pairs: [
        { left: 'She keeps', right: 'improving every week' },
        { left: 'She finished', right: 'writing her thesis' },
        { left: 'She plans', right: 'to study abroad' },
        { left: 'She expects', right: 'to get the promotion' },
      ],
      explanation: 'Keep and finish take a gerund; plan and expect take an infinitive.',
    },
  ],

  'gerunds-infinitives-special-cases': [
    {
      kind: 'multiple-choice',
      prompt: 'Carla stopped ___ for having opinions — she quit that habit for good.',
      options: ['apologizing', 'to apologize', 'apologize'],
      correctIndex: 0,
      explanation: 'Stop + gerund means quitting a habit; stop + infinitive means pausing to do something else.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'On her way to the interview, she stopped ___ a coffee and calm her thoughts.',
      options: ['getting', 'to get', 'got'],
      correctIndex: 1,
      explanation: 'Stop + infinitive: she stopped what she was doing in order to do something else.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I remembered ___ the report before the deadline — mission accomplished!',
      options: ['sending', 'to send', 'send'],
      correctIndex: 1,
      explanation: 'Remember + infinitive: something came to your mind and then you did it.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'They tried ___ the director all morning, but she never answered the phone.',
      options: ['to reach', 'reaching', 'reached'],
      correctIndex: 0,
      explanation: 'Try + infinitive implies failure — a goal that was not reached.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'We regret ___ you that the position has been filled.',
      options: ['informing', 'to inform', 'inform'],
      correctIndex: 1,
      explanation: 'Regret + infinitive refers to a present regret, something coming up right now.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She’ll never forget ___ (give) her first presentation in English — what a moment!',
      answers: ['giving'],
      explanation: 'Forget + gerund mentions a memory of something completed in the past.',
    },
  ],

  'to-vs-for': [
    {
      kind: 'multiple-choice',
      prompt: 'Ana is flying ___ Lisbon next week for a tech conference.',
      options: ['to', 'for', 'at'],
      correctIndex: 0,
      explanation: 'Use to for direction — movement toward a place: We\'re going to the zoo.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She studied every single night ___ pass the proficiency exam.',
      options: ['for', 'to', 'for to'],
      correctIndex: 1,
      explanation: 'When the reason uses a verb, use to: I study so much to get good grades.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Beatriz moved abroad ___ a better job.',
      options: ['to', 'for', 'at'],
      correctIndex: 1,
      explanation: 'When the reason uses a noun, use for: She became a doctor for the money.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'They preferred the morning class ___ the evening one.',
      options: ['for', 'than', 'to'],
      correctIndex: 2,
      explanation: 'To introduces the second part of a comparison: They preferred water to soda.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Thank you ___ believing in my project from the very beginning.',
      answers: ['for'],
      explanation: 'Use for to show thanks or gratitude: Thank you for your email.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Helena has led that team ___ five years — and it shows in the results.',
      answers: ['for'],
      explanation: 'Use for to indicate a length of time or distance: She worked there for four years.',
    },
  ],

  'substitution-avoiding-repetition': [
    {
      kind: 'multiple-choice',
      prompt: 'Two scholarships were available, and Rita applied for ___ — why choose when you can try for everything?',
      options: ['both', 'the two ones', 'both of they'],
      correctIndex: 0,
      explanation: 'Indefinite quantifying pronouns like both and either substitute nouns already mentioned.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I always review my notes after class, and my study partner does ___ too.',
      options: ['so', 'that', 'this'],
      correctIndex: 0,
      explanation: 'Do so substitutes for a verb and its complement: ...and my wife does so too.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Many people believe education transforms lives, and I think ___ too.',
      options: ['it', 'so', 'such'],
      correctIndex: 1,
      explanation: 'So replaces a whole object clause after verbs like think and believe: I think so too.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'These ideas are good, but the ___ she presented yesterday were brilliant.',
      options: ['one', 'ones', 'some'],
      correctIndex: 1,
      explanation: 'One and ones substitute countable nouns: the small red ones.',
    },
    {
      kind: 'fill-blank',
      prompt: 'There were two time slots for the mentoring session, but I didn’t like ___ — I asked for a third option.',
      answers: ['either', 'either one', 'either of them'],
      explanation: 'Either substitutes the nouns in a negative choice between two: I didn\'t like either.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Some people assume women don’t negotiate hard — but that ___ is simply wrong. (noun form of assume)',
      answers: ['assumption'],
      explanation: 'Verb-noun transformation avoids repetition: replace the verb assume with its noun equivalent.',
    },
  ],

  'one-generic-pronouns': [
    {
      kind: 'multiple-choice',
      prompt: 'One never ___ what opportunities a new language will bring.',
      options: ['know', 'knows', 'knowing'],
      correctIndex: 1,
      explanation: 'The generic pronoun one takes a third person singular verb: One never knows.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence keeps the generic pronoun consistent?',
      options: [
        'One should always trust one’s instincts.',
        'One should always trust your instincts.',
        'You should always trust one’s instincts.',
      ],
      correctIndex: 0,
      explanation: 'Once you choose one as your generic pronoun, keep using it: One never knows what one\'s future will bring.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'One of the most inspiring ___ in the course is Maria’s journey from student to business owner.',
      options: ['story', 'stories', 'storys'],
      correctIndex: 1,
      explanation: 'One of + superlative adjective is followed by a plural noun: one of the most modern cities.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Before the class, every student said ___ name aloud.',
      options: ['his', 'their', 'its'],
      correctIndex: 1,
      explanation: 'Generic they/their refers to one person who could be anyone: every student said their name.',
    },
    {
      kind: 'fill-blank',
      prompt: 'One should never apologize for ___ ambition. (possessive of one)',
      answers: ['one’s', 'one\'s', 'ones'],
      explanation: 'The possessive determiner one\'s means “your” in generic statements and is always followed by a noun.',
    },
    {
      kind: 'fill-blank',
      prompt: 'A good journalist should never reveal where ___ get their information.',
      answers: ['they'],
      explanation: 'They is used as a singular generic pronoun for a person who could be male or female.',
    },
  ],

  'causative-sentences': [
    {
      kind: 'multiple-choice',
      prompt: 'Her mentor made her ___ the speech one more time before the big day.',
      options: ['practice', 'to practice', 'practicing'],
      correctIndex: 0,
      explanation: 'Make takes the base form of the verb: She made him apologize.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'My boss let me ___ the client meeting on my own — and I nailed it.',
      options: ['to lead', 'lead', 'leading'],
      correctIndex: 1,
      explanation: 'Let also takes the base form, with no to: She let me drive her car.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She got her lawyer ___ the contract before she signed anything.',
      options: ['review', 'to review', 'reviewing'],
      correctIndex: 1,
      explanation: 'In active causatives, get takes to + infinitive: He got her to read the book.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I ___ my hair cut the day before the job interview.',
      options: ['made', 'did', 'had'],
      correctIndex: 2,
      explanation: 'Passive causative: have/get + object + past participle, when the focus is on the action, not who does it.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Before the international conference, Laura got her presentation ___ (translate) into English.',
      answers: ['translated'],
      explanation: 'Passive causative: get + object + past participle — she caused it to be done by someone.',
    },
    {
      kind: 'matching',
      instruction: 'Match each causative verb with the correct continuation:',
      pairs: [
        { left: 'She made', right: 'us rehearse the pitch' },
        { left: 'She let', right: 'us choose the topic' },
        { left: 'She got', right: 'us to arrive early' },
      ],
      explanation: 'Make and let take the base form; get takes to + infinitive.',
    },
  ],

  'english-collocations': [
    {
      kind: 'multiple-choice',
      prompt: 'Don’t be afraid — everyone ___ mistakes when learning a new language.',
      options: ['does', 'makes', 'commits'],
      correctIndex: 1,
      explanation: 'The collocation is make a mistake — not do a mistake.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Successful women aren’t afraid to ___ risks.',
      options: ['get', 'make', 'take'],
      correctIndex: 2,
      explanation: 'The collocation is take risks — not get risks.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'We canceled the outdoor workshop because of the ___ rain.',
      options: ['strong', 'heavy', 'hard'],
      correctIndex: 1,
      explanation: 'We say heavy rain, not strong rain — collocations are predictable combinations of words.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Her team ___ a deal worth two million dollars last quarter.',
      options: ['landed', 'did', 'made up'],
      correctIndex: 0,
      explanation: 'In business English, we land a deal or close a deal.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Rafaela is ___ (make) real progress on her negotiation skills.',
      answers: ['making'],
      explanation: 'The collocation is make progress: We\'re making progress on the project at work.',
    },
    {
      kind: 'matching',
      instruction: 'Match each verb with the word it collocates with:',
      pairs: [
        { left: 'make', right: 'a big decision' },
        { left: 'take', right: 'a risk' },
        { left: 'open', right: 'an account' },
        { left: 'save', right: 'time' },
      ],
      explanation: 'There are no rules for collocations — they are combinations we become familiar with and use correctly.',
    },
  ],

  'debating-useful-language': [
    {
      kind: 'multiple-choice',
      prompt: 'In a debate, which phrase shows PARTIAL agreement?',
      options: [
        'I agree with you up to a point, but...',
        'That’s exactly how I see it too.',
        'I strongly disagree with that argument.',
      ],
      correctIndex: 0,
      explanation: 'Partial agreement accepts part of the idea before adding your own view: I agree up to a point, but...',
    },
    {
      kind: 'multiple-choice',
      prompt: 'A colleague makes a point you disagree with. The most polite way to push back is:',
      options: [
        'That’s completely wrong.',
        'I see your point, but I respectfully disagree.',
        'There’s no way that’s true.',
      ],
      correctIndex: 1,
      explanation: 'Polite disagreement acknowledges the other side first: I see your point, but I respectfully disagree.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'To support your argument with research, you can say:',
      options: [
        'According to recent studies...',
        'Let me put that another way...',
        'To sum up, we need to...',
      ],
      correctIndex: 0,
      explanation: 'Phrases like According to recent studies... and Research shows that... introduce evidence.',
    },
    {
      kind: 'fill-blank',
      prompt: 'You didn’t understand your opponent’s argument. You ask: Could you ___ that point, please?',
      answers: ['clarify'],
      explanation: 'To seek clarification, ask: Could you clarify that point, please?',
    },
    {
      kind: 'fill-blank',
      prompt: 'Closing her winning argument, Luiza says: In ___, the main point is that equal pay benefits everyone.',
      answers: ['conclusion'],
      explanation: 'To summarize, use In conclusion, the main point is... or To sum up, we need to...',
    },
    {
      kind: 'matching',
      instruction: 'Match each debate function with its phrase:',
      pairs: [
        { left: 'Stating your position', right: 'I strongly believe that...' },
        { left: 'Giving an example', right: 'Take the case of...' },
        { left: 'Rebutting', right: 'The problem with that viewpoint is that...' },
        { left: 'Summarizing', right: 'To sum up, we need to...' },
      ],
      explanation: 'Knowing a phrase for each function helps you keep your confidence in a structured debate.',
    },
  ],
};
